import React, { useMemo, useState, useCallback } from 'react';
import { useSessionStore } from '../../stores/sessionStore';
import { CardPair } from '../shared/Card';
import { ComboMatrix } from '../matrix/ComboMatrix';
import { holeCardsToCategory } from '@range-reader/poker-core';
import { PlayerTooltip } from '../players/PlayerTooltip';
import { HandHistoryColumn } from './HandHistoryColumn';
import { apiPatch } from '../../lib/api-client';

type StreetKey = 'preflop' | 'flop' | 'turn' | 'river';

function ScoreBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="score-bar-track">
      <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function StreetBadge({ street }: { street: string }) {
  const colors: Record<string, string> = {
    preflop: '#6366f1', flop: '#10b981', turn: '#f59e0b', river: '#ef4444',
  };
  return (
    <span className="street-badge" style={{ background: colors[street] ?? '#6b7280' }}>
      {street.toUpperCase()}
    </span>
  );
}

export function ShowdownScreen({ onNextHand }: { onNextHand: () => void }) {
  const hand = useSessionStore((s) => s.hand);
  const scoreBreakdown = useSessionStore((s) => s.scoreBreakdown);
  const snapshots = useSessionStore((s) => s.snapshots);
  const eliminationLog = useSessionStore((s) => s.eliminationLog);
  const sessionId = useSessionStore((s) => s.sessionId);

  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);

  const handleSaveNote = useCallback(() => {
    if (!sessionId || !note.trim()) return;
    apiPatch(`/api/sessions/${sessionId}/note`, { note: note.trim() }).catch(console.error);
    setNoteSaved(true);
  }, [sessionId, note]);

  // Range width at end of each street — must be before any early return (React hooks rules)
  const rangeWidthByStreet = useMemo(() => {
    const result: Record<string, number | null> = {};
    for (const snap of snapshots) result[snap.street] = snap.highlightedCategories.length;
    return result;
  }, [snapshots]);

  // Eliminations grouped by street
  const eliminationsByStreet = useMemo(() => {
    const grouped: Record<StreetKey, string[]> = {
      preflop: [], flop: [], turn: [], river: [],
    };
    for (const entry of eliminationLog) {
      const s = entry.street as StreetKey;
      if (s in grouped) grouped[s].push(entry.handLabel);
    }
    return grouped;
  }, [eliminationLog]);

  if (!hand || !scoreBreakdown) return null;

  const actualHand = holeCardsToCategory(hand.winner?.holeCards ?? '') ?? (hand.winner?.holeCards ?? '');
  const winner = hand.winner;

  const { hit, baseScore, nearMissCredit, nearMissDistance, streetBonus, isolationStreet, totalScore, finalRangeWidth } = scoreBreakdown;
  const scoreColor = totalScore >= 70 ? '#10b981' : totalScore >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="showdown-screen">

      {/* LEFT: hand history */}
      <div className="showdown-history">
        <HandHistoryColumn
          hand={hand}
          eliminationsByStreet={eliminationsByStreet}
          rangeWidthByStreet={rangeWidthByStreet}
        />
      </div>

      {/* CENTER: score panel */}
      <div className="showdown-center">
        <h2 className="showdown-title">Showdown</h2>

        <div className="winner-reveal">
          <div className="winner-label"><PlayerTooltip name={winner.name}>{winner.name}</PlayerTooltip> held:</div>
          <CardPair cards={winner.holeCards} faceDown={false} />
          <div className="winner-hand-desc">{winner.handDesc}</div>
        </div>

        {hand.showdownPlayers && hand.showdownPlayers.filter(
          p => p.seat !== winner.seat && p.holeCards
        ).length > 0 && (
          <div className="showdown-others">
            <div className="showdown-others-label">Also showed:</div>
            <div className="showdown-others-list">
              {hand.showdownPlayers
                .filter(p => p.seat !== winner.seat && p.holeCards)
                .map(p => (
                  <div key={p.seat} className="showdown-other-player">
                    <PlayerTooltip name={p.name}><span className="other-player-name">{p.name}</span></PlayerTooltip>
                    <CardPair cards={p.holeCards!} faceDown={false} />
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className={`hit-indicator ${hit ? 'hit' : 'miss'}`}>
          {hit ? '✓ HIT — Hand was in your range' : '✗ MISS — Hand was not in your range'}
        </div>

        <div className="score-breakdown">
          <h3>Score Breakdown</h3>

          <div className="score-row">
            <div className="score-label">
              <span>Base Score</span>
              <span className="score-hint">
                {hit ? `${finalRangeWidth} hand${finalRangeWidth !== 1 ? 's' : ''} in final range` : 'Miss — no base score'}
              </span>
            </div>
            <div className="score-value">{baseScore.toFixed(1)} <span className="score-max">/ 65</span></div>
            <ScoreBar value={baseScore} max={65} color="#3b82f6" />
          </div>

          <div className="score-row">
            <div className="score-label">
              <span>Near-Miss Credit</span>
              <span className="score-hint">
                {hit
                  ? 'N/A — hand was in range'
                  : nearMissCredit > 0
                    ? `Eliminated ${nearMissDistance === 1 ? 'last' : nearMissDistance === 2 ? '2nd-to-last' : '3rd-to-last'}`
                    : 'Eliminated too early'}
              </span>
            </div>
            <div className="score-value">{nearMissCredit} <span className="score-max">/ 10</span></div>
            <ScoreBar value={nearMissCredit} max={10} color="#8b5cf6" />
          </div>

          <div className="score-row">
            <div className="score-label">
              <span>Street Bonus</span>
              <span className="score-hint">
                {isolationStreet
                  ? <><StreetBadge street={isolationStreet} /> isolation</>
                  : 'Not isolated'}
              </span>
            </div>
            <div className="score-value">{streetBonus} <span className="score-max">/ 25</span></div>
            <ScoreBar value={streetBonus} max={25} color="#f59e0b" />
          </div>

          <div className="score-total" style={{ color: scoreColor }}>
            <span>Total Score</span>
            <span>{totalScore.toFixed(1)} <span className="score-max">/ 100</span></span>
          </div>
        </div>

        {/* Session notes */}
        <div className="session-note-area">
          <textarea
            className="session-note-input"
            placeholder="What did you learn from this hand? (optional)"
            value={note}
            onChange={e => { setNote(e.target.value); setNoteSaved(false) }}
            rows={2}
          />
          {note.trim() && (
            <button className="session-note-save" onClick={handleSaveNote}>
              {noteSaved ? 'Saved' : 'Save Note'}
            </button>
          )}
        </div>

        <button className="btn-next-hand" onClick={onNextHand}>
          Next Hand →
        </button>
      </div>

      {/* RIGHT: final matrix */}
      <div className="showdown-right">
        <h3 className="matrix-title">Your Final Range</h3>
        <p className="matrix-subtitle">
          {finalRangeWidth} hand{finalRangeWidth !== 1 ? 's' : ''} highlighted
          {hit ? ' — actual hand in gold' : ' — actual hand not in range'}
        </p>
        <ComboMatrix showActualHand={true} actualHandCategory={actualHand} />
      </div>
    </div>
  );
}
